// src/concerns/index.js  →  dist/concerns.js  (global: Concerns)
//
// Whistleblowing review (#9) — HEAD OFFICE ONLY. Lists staff concerns from
// GET /api/admin/concerns and lets HO set a status + private notes (PATCH).
// The tab is revealed only for HO (currentAdmin.role==='admin') AND the Worker
// 403s any non-HO caller — this is just the HO-facing surface. Auth = the admin
// Bearer via DB.getToken/DB.workerUrl (absolute Worker URL).

export const STATUSES = ['new', 'reviewing', 'resolved', 'closed'];

function esc(s) {
  return (typeof window !== 'undefined' && window.escapeHtml)
    ? window.escapeHtml(s)
    : String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// Pure helpers (exported for tests).
export function reporterLabel(c) {
  return (c && c.reporter_name && String(c.reporter_name).trim()) ? String(c.reporter_name).trim() : 'Anonymous';
}
export function fmtWhen(iso) {
  if (!iso) return '';
  const s = String(iso);
  return s.slice(0, 10) + ' ' + s.slice(11, 16);
}
export function statusColor(s) {
  return s === 'new' ? '#DC2626' : s === 'reviewing' ? '#D97706' : s === 'resolved' ? '#059669' : '#6b7280';
}
export function openCount(concerns) {
  return (Array.isArray(concerns) ? concerns : []).filter((c) => c && c.status !== 'closed' && c.status !== 'resolved').length;
}

function api(path, method, body) {
  const token = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  const base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = 'Bearer ' + token;
  return fetch(base + path, { cache: 'no-store', method: method || 'GET', headers, body: body ? JSON.stringify(body) : undefined });
}
function root() { return document.getElementById('concernsRoot'); }

export async function render() {
  const el = root();
  if (!el) return;
  el.innerHTML = '<div style="padding:20px;color:var(--tiny)">Loading concerns…</div>';
  let data;
  try {
    const res = await api('/api/admin/concerns');
    if (res.status === 403) { el.innerHTML = '<div style="padding:20px;color:var(--danger-deep)">Head Office access only.</div>'; return; }
    if (!res.ok) throw new Error('status ' + res.status);
    data = await res.json();
  } catch (e) {
    el.innerHTML = '<div style="padding:20px;color:var(--danger-deep)">Could not load concerns (' + esc((e && e.message) || e) + ').</div>';
    return;
  }
  draw(el, (data && data.concerns) || []);
}

function draw(el, concerns) {
  const head = '<div style="margin-bottom:14px">' +
    '<div style="font-size:18px;font-weight:800">Concerns <span style="font-size:13px;color:var(--sub);font-weight:600">(' +
      openCount(concerns) + ' open · ' + concerns.length + ' total)</span></div>' +
    '<div style="font-size:12px;color:var(--tiny)">Confidential reports from staff. Visible to Head Office only.</div></div>';
  if (!concerns.length) {
    el.innerHTML = head + '<div style="padding:20px;color:var(--tiny);border:1px dashed var(--border-strong);border-radius:10px">No concerns have been submitted.</div>';
    return;
  }
  const cards = concerns.map((c) => {
    const opts = STATUSES.map((s) => '<option value="' + s + '"' + (c.status === s ? ' selected' : '') + '>' + s + '</option>').join('');
    const contact = c.reporter_contact ? '<div style="font-size:12px;color:var(--tiny);margin-top:4px">Contact: <span translate="no">' + esc(c.reporter_contact) + '</span></div>' : '';
    const store = c.store ? ' · <span translate="no">' + esc(c.store) + '</span>' : '';
    // A named reporter stays as written; 'Anonymous' is a label and is translated.
    const who = (c.reporter_name && String(c.reporter_name).trim()) ? '<span translate="no">' + esc(reporterLabel(c)) + '</span>' : esc(reporterLabel(c));
    const cat = c.category ? '<span style="font-size:11px;background:var(--surface-3);border-radius:6px;padding:2px 7px">' + esc(c.category) + '</span>' : '';
    return '<div data-id="' + c.id + '" style="border:1px solid var(--border);border-inline-start:4px solid ' + statusColor(c.status) +
        ';border-radius:10px;padding:14px;margin-bottom:12px;background:var(--card)">' +
      '<div style="display:flex;justify-content:space-between;gap:10px;flex-wrap:wrap;align-items:center">' +
        '<div style="font-size:12px;color:var(--sub)">' + esc(fmtWhen(c.submitted_at)) + ' · ' + who + store + '</div>' + cat +
      '</div>' +
      '<div translate="no" style="white-space:pre-wrap;margin:8px 0;font-size:14px;line-height:1.5">' + esc(c.detail) + '</div>' +
      contact +
      '<div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;margin-top:10px">' +
        '<select class="cn-status" style="padding:7px;border:1px solid var(--border-strong);border-radius:8px;font-family:inherit">' + opts + '</select>' +
        '<button class="btn btn-green cn-save" onclick="Concerns.save(' + c.id + ')">Save</button>' +
        '<span class="cn-msg" style="font-size:12px;color:var(--ap-teal-text)"></span>' +
      '</div>' +
      '<textarea class="cn-notes" rows="2" placeholder="Head Office notes (private)" ' +
        'style="width:100%;margin-top:8px;padding:8px;border:1px solid var(--border-strong);border-radius:8px;font-family:inherit;font-size:13px;resize:vertical">' +
        esc(c.ho_notes || '') + '</textarea>' +
    '</div>';
  }).join('');
  el.innerHTML = head + cards;
}

export async function save(id) {
  const card = document.querySelector('#concernsRoot [data-id="' + id + '"]');
  if (!card) return;
  const status = card.querySelector('.cn-status').value;
  const notes = card.querySelector('.cn-notes').value;
  const msg = card.querySelector('.cn-msg');
  if (msg) { msg.style.color = 'var(--tiny)'; msg.textContent = 'Saving…'; }
  try {
    const res = await api('/api/admin/concerns/' + id, 'PATCH', { status, ho_notes: notes });
    if (!res.ok) throw new Error('status ' + res.status);
    if (msg) { msg.style.color = 'var(--ap-teal-text)'; msg.textContent = 'Saved.'; setTimeout(() => { msg.textContent = ''; }, 1500); }
    card.style.borderInlineStartColor = statusColor(status);
  } catch (e) {
    if (msg) { msg.style.color = 'var(--danger-deep)'; msg.textContent = 'Save failed.'; }
  }
}
